import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import DisplayType from "utils/displayType";
import { PublicPortfolio, toPublicStorageUrl } from "types/public";
import { getStartup, getNewsByStartupId } from "lib/latticeApi";
import { NewsProps } from "types/news";
import { NotionTextarea } from "admin/component/input/NotionTextarea";
import { getYMD, updateTitle } from "utils/commonUtil";
import { useLanguage } from "i18n/LanguageContext";
import { toNewsProps } from "utils/newsMapper";

import Label from "component/label/Label";
import NewsItem from "router/portfolio/component/NewsItem";
import { useLocation } from "react-router-dom";

import GoList from "component/go-list/GoList";

const PortfolioDetail = () => {
  const mode = DisplayType();
  const { t } = useLanguage();

  const { pathname } = useLocation();

  const portfolioId = useRef<string>(pathname.split("/")[2]);
  const [portfolioInfo, updatePortfolioInfo] = useState<
    PublicPortfolio | undefined
  >(undefined);
  const [portfolioNews, updatePortfolioNews] = useState<NewsProps[]>([]);

  const getPortfolioNews = async (startupId: string) => {
    try {
      const { news } = await getNewsByStartupId(startupId, 1, 3);
      updatePortfolioNews(news.map((row) => toNewsProps(row)));
    } catch (error) {
      console.log(error);
    }
  };

  const getPortfolioInfo = async () => {
    try {
      const data = await getStartup(portfolioId.current);

      updatePortfolioInfo(data);
      updateTitle(data.name);
      if (data.id) {
        getPortfolioNews(data.id);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getPortfolioInfo();
  }, []);

  if (portfolioInfo === undefined) return null;

  const logoUrl = toPublicStorageUrl(
    portfolioInfo.profile_image?.[0]?.object_url
  );

  return (
    <Container className="scroll__invisible">
      <PortfolioContainer className="limit__body scroll__invisible">
        <PortfolioHeader>
          <div className="portfolio__logo__container">
            {logoUrl ? (
              <img className="portfolio__logo" src={logoUrl} alt="logo" />
            ) : (
              <div
                className="font-sub-title2"
                style={{
                  width: "100%",
                  height: "100%",
                  color: `var(--blue-01)`,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {portfolioInfo.name.split(" ").map((item, index) => {
                  return <span key={index}>{item}</span>;
                })}
              </div>
            )}
          </div>

          <div className="portfolio__infomation">
            <div
              className={`portfolio__infomation__title ${
                mode === "Mobile" ? "font-sub-title" : "font-bold-48"
              }`}
            >
              {portfolioInfo.name}
            </div>
            <div
              className={`portfolio__infomation__introduction ${
                mode === "Mobile" ? "font-body3" : "font-medium-26"
              }`}
            >
              {portfolioInfo.description}
            </div>
            <div className="portfolio__infomation__chips__container">
              {portfolioInfo.industry_names?.map((name) => (
                <Label key={name} labelType="type1">
                  {name}
                </Label>
              ))}
            </div>
          </div>
        </PortfolioHeader>
        <PortfolioBody>
          <NotionTextarea
            className={mode === "Mobile" ? "font-body4" : "font-body2"}
            editable={false}
            defaultValue={portfolioInfo.introduction}
          />
          <div className="portfolio__company__info">
            <div className="portfolio__company__info__row">
              <span
                className={`portfolio__company__info__title ${
                  mode === "Mobile" ? "font-body4" : "font-body2"
                }`}
              >
                {t("portfolio", "detail.ceo")}
              </span>
              <span
                className={`${
                  mode === "Mobile" ? "font-bold-14" : "font-bold-18"
                }`}
                style={{ color: "var(--grey-60)" }}
              >
                {portfolioInfo.ceo_name}
              </span>
            </div>
            <div className="portfolio__company__info__row">
              <span
                className={`portfolio__company__info__title ${
                  mode === "Mobile" ? "font-body4" : "font-body2"
                }`}
              >
                {t("portfolio", "detail.location")}
              </span>
              <span
                className={`${
                  mode === "Mobile" ? "font-bold-14" : "font-bold-18"
                }`}
                style={{ color: "var(--grey-60)" }}
              >{`${
                [
                  portfolioInfo.address?.country,
                  portfolioInfo.address?.address_line,
                ]
                  .filter(Boolean)
                  .join(" ") || ""
              }`}</span>
            </div>
            <div className="portfolio__company__info__row">
              <span
                className={`portfolio__company__info__title ${
                  mode === "Mobile" ? "font-body4" : "font-body2"
                }`}
              >
                {t("portfolio", "detail.established")}
              </span>
              <span
                className={`${
                  mode === "Mobile" ? "font-bold-14" : "font-bold-18"
                }`}
                style={{ color: "var(--grey-60)" }}
              >
                {portfolioInfo.establishment_date
                  ? getYMD(portfolioInfo.establishment_date)
                  : ""}
              </span>
            </div>
            {portfolioInfo.homepage_url && (
              <div className="portfolio__company__info__row">
                <span
                  className={`portfolio__company__info__title ${
                    mode === "Mobile" ? "font-body4" : "font-body2"
                  }`}
                >
                  {t("portfolio", "detail.website")}
                </span>
                <a
                  className={`${
                    mode === "Mobile" ? "font-bold-14" : "font-bold-18"
                  }`}
                  style={{ color: "var(--primary)" }}
                  href={portfolioInfo.homepage_url}
                  target={"_blank"}
                  rel="noreferrer"
                >
                  {`${portfolioInfo.name} ${t(
                    "portfolio",
                    "detail.websiteLink"
                  )}`}
                </a>
              </div>
            )}
          </div>
          {portfolioNews.length > 0 && (
            <NewsContainer>
              <div className="font-medium-24 news__header__container">
                Related News
              </div>
              <div className="news__list">
                {portfolioNews.map((item) => (
                  <a
                    key={item.hpNewsId}
                    href={item.linkUrl}
                    target={"_blank"}
                    style={{ textDecoration: "none" }}
                    rel="noreferrer"
                  >
                    <NewsItem news={item} />
                  </a>
                ))}
              </div>
            </NewsContainer>
          )}
          <PortfolioDetailFooter>
            <GoList to={"/"} />
          </PortfolioDetailFooter>
        </PortfolioBody>
      </PortfolioContainer>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100vh;
`;

const PortfolioContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: scroll;
  background-color: var(--white);
`;

const PortfolioHeader = styled.div`
  width: 100%;
  height: fit-content;
  padding: 180px 80px 80px 80px;
  background-color: var(--white);

  display: flex;
  flex-direction: row;
  gap: 56px;

  .portfolio__logo__container {
    width: 100%;
    min-width: 220px;
    max-width: 220px;
    height: 100%;
    min-height: 220px;
    max-height: 220px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(--white);
    border: 1px solid var(--grey-00);
    .portfolio__logo {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .portfolio__infomation {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    .portfolio__infomation__title {
      margin-top: 20px;
      color: var(--black);
    }
    .portfolio__infomation__introduction {
      margin-top: 16px;
      color: var(--grey-50);
    }
    .portfolio__infomation__chips__container {
      margin-top: 32px;
      display: flex;
      flex-direction: row;
      gap: 10px;
    }
  }

  @media only screen and (max-width: 768px) {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 100px 24px 40px 24px;
    gap: 0;

    .portfolio__logo__container {
      width: 100%;
      min-width: 167px;
      max-width: 167px;
      height: 100%;
      min-height: 167px;
      max-height: 167px;
    }
    .portfolio__infomation {
      align-items: center;
      .portfolio__infomation__title {
        margin-top: 40px;
      }
      .portfolio__infomation__introduction {
        margin-top: 4px;
      }
      .portfolio__infomation__chips__container {
        margin-top: 32px;
        display: flex;
        flex-direction: row;
        gap: 10px;
      }
    }
  }
`;

const PortfolioBody = styled.div`
  width: 100%;
  flex-grow: 1;
  padding: 60px 80px 80px 80px;
  display: flex;
  flex-direction: column;

  .portfolio__company__info {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 7px;
    .portfolio__company__info__row {
      display: flex;
      flex-direction: row;
      align-items: center;
    }
    .portfolio__company__info__title {
      width: 120px;
      min-width: 120px;
      max-width: 120px;
      color: var(--grey-40);
    }
  }
  @media only screen and (max-width: 768px) {
    padding: 24px 24px 40px 24px;
    .portfolio__company__info {
      .portfolio__company__info__title {
        width: 64px;
        min-width: 64px;
        max-width: 64px;
      }
    }
  }
`;

const NewsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 100px;
  .news__header__container {
    color: var(--black);
    border-bottom: 1px solid var(--grey-00);
    padding-bottom: 24px;
  }
  .news__list {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  @media only screen and (max-width: 768px) {
    margin-top: 80px;
    gap: 12px;
    .news__header__container {
      border-bottom: none;
      padding-bottom: 0px;
    }
    .news__list {
      width: 100%;
      display: flex;
      flex-direction: column;
    }
  }
`;

const PortfolioDetailFooter = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-end;
  border-top: 1px solid var(--grey-10);
  margin-top: 40px;
  margin-bottom: 160px;
  padding-top: 16px;

  @media only screen and (max-width: 768px) {
    margin-top: 40px;
    margin-bottom: 24px;
  }
`;
export default PortfolioDetail;
